import type { AccountBalanceEntity } from '@actual-app/core/types/models';

// ISO 20022 CAMT balance-type codes -- universal across every ASPSP Enable
// Banking supports, not specific to any one bank. This is what keeps the
// display logic bank-agnostic: a new bank reporting an unfamiliar type just
// falls through and contributes nothing, rather than needing a new case
// added here (see wailmu/actual#20).
//
// Ordered by priority, not just membership -- a bank can report both a
// closing (CLBD/CLAV, previous cycle/end-of-day) and interim (ITBD/ITAV,
// right now) variant simultaneously, and querying an unordered table gives
// no guarantee which one a plain array .find() would land on first. Interim
// is preferred since it reflects the current moment, matching what
// "available now" should mean.
const BOOKED_TYPE_PRIORITY = ['ITBD', 'CLBD'];
const AVAILABLE_TYPE_PRIORITY = ['ITAV', 'CLAV'];

function findByPriority(
  entries: AccountBalanceEntity[],
  priority: string[],
): AccountBalanceEntity | undefined {
  for (const type of priority) {
    const found = entries.find(b => b.balance_type === type);
    if (found) return found;
  }
  return undefined;
}

export type PendingBalance =
  | { kind: 'pending'; available: number; pending: number; currency: string }
  | {
      kind: 'difference';
      available: number;
      difference: number;
      currency: string;
    };

/**
 * Only returns a value when a bank has reported *both* a booked-like and an
 * available-like balance for this account -- if it only reports one type
 * (of any kind), there's nothing to compute from, so this returns null and
 * callers should show nothing extra rather than a bogus "pending: 0".
 *
 * `booked - available` is normally >= 0 (a hold reduces what's available
 * below the booked/settled balance) -- that's the "pending" case, and holds
 * regardless of account type.
 *
 * A negative delta is ambiguous: it could mean a credit account (the
 * bank's "available" is remaining credit limit, not spendable cash --
 * confirmed against real Danske Credit data), OR it could just as well
 * mean a checking/savings account with an incoming pending deposit not yet
 * booked (available > booked is a perfectly normal thing for a bank to
 * report in that case too). Sign alone can't tell these apart, and Actual
 * has no reliable per-account signal today to disambiguate (no account
 * `type` field exists, and `balance_limit` exists in the schema but isn't
 * populated by anything) -- so this deliberately does *not* guess "credit"
 * and shows a neutral difference instead. Revisit if/when a real signal
 * exists (see wailmu/actual#21's review discussion).
 */
export function getPendingBalance(
  balances: AccountBalanceEntity[],
  accountId: string,
): PendingBalance | null {
  const forAccount = balances.filter(b => b.account_id === accountId);
  const booked = findByPriority(forAccount, BOOKED_TYPE_PRIORITY);
  const available = findByPriority(forAccount, AVAILABLE_TYPE_PRIORITY);

  if (!booked || !available) {
    return null;
  }

  const delta = booked.amount - available.amount;

  if (delta < 0) {
    return {
      kind: 'difference',
      available: available.amount,
      difference: available.amount - booked.amount,
      currency: available.currency,
    };
  }

  return {
    kind: 'pending',
    available: available.amount,
    pending: delta,
    currency: available.currency,
  };
}

// Balance-fetch runs 4x/day (5 minutes after each bank sync), so 24h
// tolerates one or two transient failures (e.g. a bank's 429) without
// flagging, while still surfacing a genuinely dead connection -- like a
// bank-side session revocation -- within a day (see wailmu/actual#37).
export const STALE_BANK_DATA_AFTER_MS = 24 * 60 * 60 * 1000;

export type BankDataFreshness = {
  /** Most recent `fetched_at` across this account's balance entries. */
  fetchedAt: string;
  isStale: boolean;
};

/**
 * How current this account's bank-reported data is, judged by the newest
 * `fetched_at` among its balance entries -- a failed fetch leaves the
 * previous rows (and their timestamp) untouched, so this ages naturally
 * when a connection breaks.
 *
 * Returns null for accounts that have never had any bank-reported balance
 * (manual/off-budget accounts such as pensions), which have nothing to be
 * stale relative to and must never be flagged.
 */
export function getBankDataFreshness(
  balances: AccountBalanceEntity[],
  accountId: string,
  now: number,
): BankDataFreshness | null {
  let newest: string | null = null;
  let newestMs = -Infinity;
  for (const b of balances) {
    if (b.account_id !== accountId) continue;
    const ms = Date.parse(b.fetched_at);
    if (Number.isNaN(ms)) continue;
    if (ms > newestMs) {
      newestMs = ms;
      newest = b.fetched_at;
    }
  }
  if (newest == null) {
    return null;
  }
  return {
    fetchedAt: newest,
    isStale: now - newestMs > STALE_BANK_DATA_AFTER_MS,
  };
}
